/**
 * Builds a `resultEqualityCheck` for a selector that returns a fresh array of
 * fresh objects on every recompute.
 *
 * Such a selector recomputes whenever its input moves — `spreads` moves on
 * every keystroke — and hands back an array no reference check can see through,
 * so a caller comparing with `shallowEqual` re-renders on edits that changed
 * nothing it reads. Comparing item by item lets the PREVIOUS array come back
 * when the recompute produced the same answer, and the caller's own check then
 * holds.
 *
 * Array-guarded because reselect's dev-mode input-stability check reuses the
 * comparator against two throwaway `{}` values of its own, and would otherwise
 * die on `.every`. Answering false for those is what that check wants: it only
 * asks whether the SAME arguments produced the same reference, which they
 * still do, so the guard costs nothing and raises no warning.
 */
function sameArrayResult<T>(itemsEqual: (a: T, b: T) => boolean) {
  return (a: T[], b: T[]): boolean =>
    Array.isArray(a) &&
    Array.isArray(b) &&
    a.length === b.length &&
    a.every((item, index) => itemsEqual(item, b[index]));
}

export default sameArrayResult;
